import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowsCounterClockwise } from "@phosphor-icons/react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { advisorHeaders } from "../lib/clarity";
import AudioPreloadCard from "../components/AudioPreloadCard";
import ReferralsTable from "../components/ReferralsTable";
import { SignupFunnel } from "../components/SignupFunnel";

const OUTCOMES = [["pending", "Pending"], ["showed", "Showed"], ["no_show", "No-show"], ["rescheduled", "Rescheduled"]];
const LBL = { retirement_income: "Retirement income", protection: "Protection", rollover: "Rollover", legacy: "Legacy", "0-6": "0–6 mo", "6-18": "6–18 mo", "18+": "18+ mo" };
const pct = (v) => (v == null ? "—" : `${v}%`);

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="card-surface p-5">
    <p className="eyebrow !text-[10px] !text-exodus-muted">{label}</p>
    <p className="font-display text-4xl gold-text mt-2">{value}</p>
    {sub && <p className="text-[11px] text-exodus-muted mt-1">{sub}</p>}
  </div>
);

export default function AdminRetention() {
  const [m, setM] = useState(null);
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/admin/retention", advisorHeaders()), api.get("/admin/retention/bookings", advisorHeaders())]);
      setM(a.data); setRows(b.data.items);
    } catch { toast.error("Could not load retention metrics. Sign in with an advisor account."); }
  };
  useEffect(() => { load(); }, []);

  const setOutcome = async (id, outcome) => {
    try {
      await api.patch(`/admin/retention/bookings/${id}`, { outcome }, advisorHeaders());
      toast.success("Booking outcome saved."); load();
    } catch { toast.error("Could not save outcome."); }
  };

  return (
    <DashboardLayout>
      <div data-testid="admin-retention-page" className="p-6 lg:p-10">
        <div className="mb-8 pl-12 lg:pl-0">
          <p className="eyebrow flex items-center gap-2"><ArrowsCounterClockwise size={16} /> Retention Ladder</p>
          <h1 className="font-display text-4xl mt-2">Fit → Calculator → Illustration Review</h1>
          <p className="text-sm text-exodus-muted mt-2">Weekly success metrics. Funnel rates count users tracked since the Retention Ladder launched.</p>
        </div>
        {m && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="ret-returning-7d" label="7-day returning users" value={m.returning_7d} sub={`of ${m.active_7d} active · ${pct(m.returning_rate_7d)} · prev wk ${m.returning_prev_7d}/${m.active_prev_7d}`} />
            <Stat testid="ret-fit-rate" label="Find My Fit completion" value={pct(m.fit_completion_rate)} sub={`${m.fit_completed} completed · ${m.fit_started} started`} />
            <Stat testid="ret-book-from-fit" label="Booking rate · from Fit" value={pct(m.booking_rate_from_fit)} sub={`${m.paid_bookings} paid bookings total`} />
            <Stat testid="ret-book-from-calc" label="Booking rate · from Calculator" value={pct(m.booking_rate_from_calc)} sub={`${m.calc_completed} calculator completions`} />
            <Stat testid="ret-no-show" label="No-show rate" value={pct(m.no_show_rate)} sub={`${m.outcomes.no_show} no-show · ${m.outcomes.showed} showed`} />
            <Stat testid="ret-reschedule" label="Reschedule rate" value={pct(m.reschedule_rate)} sub={`${m.outcomes.rescheduled} rescheduled · ${m.outcomes.pending} pending`} />
            <Stat testid="ret-signups" label="New accounts · 7 days" value={m.signups_7d} />
            <Stat testid="ret-checkins" label="Check-ins · 7 days" value={m.checkins_7d} />
          </div>
        )}
        <SignupFunnel />
        <AudioPreloadCard />
        <ReferralsTable />
        <h2 className="font-display text-2xl mt-12 mb-4">Paid bookings — mark the outcome</h2>
        <div className="border border-exodus-border overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-exodus-elevated"><tr className="text-left text-[10px] uppercase tracking-widest text-exodus-muted">
              <th className="p-3">Booked</th><th className="p-3">Client</th><th className="p-3">Pre-call packet</th><th className="p-3">Outcome</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-exodus-muted">No paid bookings yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`ret-booking-${r.id}`} className="border-t border-exodus-border align-top">
                  <td className="p-3 text-xs text-exodus-muted whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                  <td className="p-3"><p>{r.name || "—"}</p><p className="text-xs text-exodus-muted">{r.email} {r.phone ? `· ${r.phone}` : ""}</p></td>
                  <td className="p-3 text-xs text-exodus-muted">{r.precall ? `${r.precall.state || "—"} · ${LBL[r.precall.primary_goal] || "—"} · ${LBL[r.precall.timeline] || "—"}` : "—"}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {OUTCOMES.map(([k, l]) => (
                        <button key={k} data-testid={`ret-outcome-${r.id}-${k}`} onClick={() => setOutcome(r.id, k)}
                          className={`text-[11px] px-2.5 py-1 border transition-colors ${r.outcome === k ? "border-exodus-gold bg-exodus-gold text-black" : "border-exodus-border text-exodus-muted hover:border-exodus-gold"}`}>{l}</button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
